import { useEffect, useRef, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * SlideOver — the house right-anchored panel (extracted from the 360
 * good-to-know slide-out so item/detail editors share one pattern). Closes on
 * Escape, backdrop click, and the X. Header + scrollable body + optional sticky
 * footer. Use for detail/edit surfaces that should keep the list visible.
 */
interface SlideOverProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const sizeClasses = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" };

export function SlideOver({
  open, onClose, title, subtitle, children, footer, size = "md",
}: SlideOverProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    // KEYS-01: Esc closes an open pick-list first (its own handler does that);
    // only a second Esc closes the panel.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if ((document.activeElement as HTMLElement | null)?.getAttribute("aria-expanded") === "true") return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // KEYS-01: the panel opens with the cursor in its first field, not on the X.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      const body = bodyRef.current;
      if (!body || body.contains(document.activeElement)) return;
      const pick =
        body.querySelector<HTMLElement>("[data-autofocus]") ??
        body.querySelector<HTMLElement>('input:not([type="hidden"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([readonly]):not([disabled]), [role="combobox"]') ??
        body.querySelector<HTMLElement>('button:not([disabled]):not([tabindex="-1"]), a[href]');
      pick?.focus();
      if (pick instanceof HTMLInputElement && pick.value && (pick.type === "text" || pick.type === "search")) pick.select();
    }, 260); // after the slide-in
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[70]">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              "absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl",
              sizeClasses[size],
            )}
            role="dialog" aria-modal="true"
          >
            {(title || subtitle) && (
              <header className="flex items-start justify-between gap-3 border-b border-hairline px-5 py-4">
                <div className="min-w-0">
                  {title && <h2 className="truncate text-base font-semibold text-slate-900">{title}</h2>}
                  {subtitle && <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p>}
                </div>
                <button
                  type="button" onClick={onClose} aria-label="Close"
                  className="-mr-1 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </header>
            )}
            <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
            {footer && <div className="border-t border-hairline bg-white px-5 py-3">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
